import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import * as alertService from '../services/alertService';

// Alert-specific interfaces
export interface AlertFilters {
  severity?: 'low' | 'medium' | 'high' | 'critical' | 'info';
  status?: 'active' | 'investigating' | 'resolved' | 'dismissed' | 'escalated';
  alert_type?: string;
  school_id?: string;
  page?: number;
  per_page?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  start_date?: string;
  end_date?: string;
}

export interface CreateAlertData {
  alert_type: string;
  severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
  title: string;
  description: string;
  location?: string;
  student_id?: number;
  camera_id?: number;
  metadata?: Record<string, any>;
}

export interface UpdateAlertData {
  notes?: string;
  action_taken?: string;
  reason?: string;
}

export interface EscalateAlertData {
  level: string;
  notes: string;
}


// Hooks
export const useAlerts = (filters?: AlertFilters) => {
  return useQuery({
    queryKey: ['alerts', filters],
    queryFn: async () => {
      const response = await alertService.getAlerts(filters);
      return response;
    },
    select: (data) => {
      // API returns { alerts: Alert[], pagination: Pagination }
      // Return consistent structure with alerts array
      return {
        alerts: data.alerts || [],
        pagination: data.pagination || null
      };
    },
    refetchInterval: 30000, // Refetch every 30 seconds
  });
};

export const useAlert = (alertId: string) => {
  return useQuery({
    queryKey: ['alerts', alertId],
    queryFn: async () => {
      const response = await alertService.getAlert(alertId);
      return response;
    },
    enabled: !!alertId,
  });
};

// Note: Alert creation is typically handled by the system automatically
// This hook is kept for potential manual alert creation scenarios
export const useCreateAlert = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (_data: CreateAlertData) => {
      // Since there's no create endpoint in alertService, this would need to be implemented
      // For now, we'll throw an error to indicate this functionality isn't available
      throw new Error('Manual alert creation not implemented in the API');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      notifications.show({
        title: 'Alert Created',
        message: 'New alert has been created and notifications sent',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Creation Failed',
        message: error.message || 'Failed to create alert. Please try again.',
        color: 'red',
      });
    },
  });
};

// Acknowledge alert hook
export const useAcknowledgeAlert = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, notes }: { id: string; notes?: string }) => {
      const response = await alertService.acknowledgeAlert(id, notes);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      notifications.show({
        title: 'Alert Acknowledged',
        message: 'Alert has been acknowledged successfully',
        color: 'green',
      });
    },
    onError: () => {
      notifications.show({
        title: 'Acknowledgment Failed',
        message: 'Failed to acknowledge alert. Please try again.',
        color: 'red',
      });
    },
  });
};

// Resolve alert hook
export const useResolveAlert = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: { notes: string; action_taken?: string } }) => {
      const response = await alertService.resolveAlert(id, data);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      notifications.show({
        title: 'Alert Resolved',
        message: 'Alert has been resolved successfully',
        color: 'green',
      });
    },
    onError: () => {
      notifications.show({
        title: 'Resolution Failed',
        message: 'Failed to resolve alert. Please try again.',
        color: 'red',
      });
    },
  });
};

// Dismiss alert hook
export const useDismissAlert = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      const response = await alertService.dismissAlert(id, reason);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      notifications.show({
        title: 'Alert Dismissed',
        message: 'Alert has been dismissed successfully',
        color: 'green',
      });
    },
    onError: () => {
      notifications.show({
        title: 'Dismissal Failed',
        message: 'Failed to dismiss alert. Please try again.',
        color: 'red',
      });
    },
  });
};

// Update alert hook (general update)
export const useUpdateAlert = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateAlertData }) => {
      // Since there's no general update endpoint, we'll use acknowledge for basic updates
      const response = await alertService.acknowledgeAlert(id, data.notes);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      notifications.show({
        title: 'Alert Updated',
        message: 'Alert has been updated successfully',
        color: 'green',
      });
    },
    onError: () => {
      notifications.show({
        title: 'Update Failed',
        message: 'Failed to update alert. Please try again.',
        color: 'red',
      });
    },
  });
};

export const useEscalateAlert = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: EscalateAlertData }) => {
      const response = await alertService.escalateAlert(id, data);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      notifications.show({
        title: 'Alert Escalated',
        message: 'Alert has been escalated and relevant parties notified',
        color: 'orange',
      });
    },
    onError: () => {
      notifications.show({
        title: 'Escalation Failed',
        message: 'Failed to escalate alert. Please try again.',
        color: 'red',
      });
    },
  });
};

// Alert statistics hook
export const useAlertStats = (filters?: { school_id?: string; date_range?: string }) => {
  return useQuery({
    queryKey: ['alert-stats', filters],
    queryFn: async () => {
      const response = await alertService.getAlertStats(filters);
      return response;
    },
  });
};